import React, { useContext } from 'react'
import { t } from '@lingui/macro'
import { DownloadCSVButton } from '../../../components/common/download-csv-button/DownloadCSVButton'
import { IWorkbooksTableFilter } from './WorkbooksOverviewTable'
import { stripHTMLTags } from 'shared/HTML'
import {
  WorkbookCsvQuery,
  WorkbookCsvDocument
} from './DownloadWorkbooksCSV.codegen'
import { AnalyticsContext } from '../AnalyticsContext'
import {
  formatTimeInSec,
  getAttemptString,
  getScoreString
} from 'front-end/src/pages/analytics/utils'

export const getListOfAllKeys = (rows: Array<any>) => {
  const objectsKeys = rows.map((r) => Object.keys(r))
  // Merge all keys into one array and remove duplicates using Set
  return new Set(([] as Array<string>).concat(...objectsKeys))
}

interface IDownloadWorkbooksCSVProps {
  workbooksOverviewFilter: IWorkbooksTableFilter
  workbooksCount: number
}

export const prepareWorkbooksCSVData = (
  data: WorkbookCsvQuery['analytics']['workbooks']['workbooksOverview']
) => {
  const rows = data.map((row) => {
    const { enrolment } = row
    const { user, workspace } = enrolment
    const {
      assigned_at,
      available_points,
      completion_date,
      pass_mark,
      points_acquired,
      status,
      start_date,
      target_completion_date,
      topic_id,
      created_at
    } = enrolment

    const result = {
      [t`Display name`]: user.display_name,
      [t`Email`]: user.email,
      [t`First name`]: user.first_name,
      [t`Last name`]: user.last_name,
      [t`Position`]: user.position,
      [t`Employee ID`]: user.employee_id,
      [t`Area`]: user.area,
      [t`Location`]: user.location,
      [t`Department`]: user.department,
      [t`Custom 1`]: user.custom_1,
      [t`Custom 2`]: user.custom_2,
      [t`Custom 3`]: user.custom_3,
      [t`Account name`]: user.account_name,
      [t`Last active`]: user.last_activity_at,
      [t`User deleted at`]: user.deleted_at,
      [t`Resource title`]: row.resourceName,
      [t`Resource ID`]: topic_id,
      [t`Resource version`]: enrolment.topicVersion?.version,
      [t`Resource version publication date`]:
        enrolment.topicVersion?.created_at,
      [t`Workspace name`]: workspace?.name,
      [t`Workspace ID`]: workspace?.id,
      [t`Created at`]: created_at,
      [t`Assigned at`]: assigned_at,
      [t`Target completion date`]: target_completion_date,
      [t`Start date`]: start_date,
      [t`Completion date`]: completion_date,
      [t`Available points`]: available_points,
      [t`Pass mark`]: pass_mark,
      [t`Points acquired`]: points_acquired,
      [t`Resource read time minutes`]: enrolment.topicVersion?.readDurationMins,
      [t`Status`]: status,
      [t`Attempt`]: getAttemptString(
        enrolment.attempt,
        enrolment.topicVersion?.max_attempts,
        !!enrolment.topicVersion?.allow_retry,
        t`Unlimited`
      ),
      [t`Time spent`]: formatTimeInSec(enrolment.progress_time),
      [t`Score`]: getScoreString(points_acquired, available_points),
      [t`Rating`]: enrolment.rating,
      [t`Feedback`]: enrolment.feedback,
      [t`Workbook deleted at`]: enrolment.deleted_at,
      [t`Manager ID`]: user.manager?.id,
      [t`Manager email`]: user.manager?.email,
      [t`Manager name`]: user.manager?.display_name
    }

    // quizResponses are included only when exporting workbooks for specific resource
    if (enrolment?.quizResponses && enrolment?.quizResponses.length) {
      enrolment?.quizResponses.map((quizResponse) => {
        let response: string | null = null
        if (quizResponse.freeQuizResponse) {
          response = quizResponse.freeQuizResponse
        } else if (quizResponse.optionResponses) {
          response = quizResponse.optionResponses
            .filter(({ value }) => value)
            .map(
              ({ optionContent }) =>
                optionContent && stripHTMLTags(optionContent)
            ) // omit HTML and leave raw text content
            .join(', ')
        }
        const quizQuestion = stripHTMLTags(quizResponse.topicElement.content)
        result[quizQuestion] = response
      })
    }

    return result
  })

  const exportKeys = getListOfAllKeys(rows)

  return rows.map((row) => {
    const result = {}
    exportKeys.forEach((key) => (result[key] = row[key]))
    return result
  })
}

export const DownloadWorkbooksCSV = ({
  workbooksOverviewFilter,
  workbooksCount
}: IDownloadWorkbooksCSVProps) => {
  const { filterAsQueryVariables } = useContext(AnalyticsContext)

  return (
    <DownloadCSVButton
      count={workbooksCount}
      fileName={'looop-workbooks'}
      dataPath={'data.analytics.workbooks.workbooksOverview'}
      customBatchSize={500}
      queryOptions={(page, limit) => ({
        variables: {
          mainAnalyticsFilter: filterAsQueryVariables,
          workbooksOverviewFilter: {
            ...workbooksOverviewFilter,
            page,
            limit
          },
          includeQuizResponses: !!workbooksOverviewFilter.resourceId
        },
        query: WorkbookCsvDocument,
        fetchPolicy: 'no-cache'
      })}
      prepareData={prepareWorkbooksCSVData}
    />
  )
}
